// Global imports
import { clsx } from 'clsx'
import { Button, ContentEditor, Image } from '~/components'
import { backgroundColourClass } from '~/utils'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type { SanityButtonProps, SanityRefWithAlt } from '~/types'

interface BlockBannerHeroProps {
  content?: PortableTextBlock[]
  image?: SanityRefWithAlt
  mobileImage?: SanityRefWithAlt
  button?: SanityButtonProps
  buttonSecondary?: SanityButtonProps
  backgroundColor?: string
}

// Main export
export default function BlockBannerHero({
  content,
  image,
  mobileImage,
  button,
  buttonSecondary,
  backgroundColor = 'vibrant-green',
}: BlockBannerHeroProps) {
  const hasImage = !!image?.asset
  const hasButtons = !!(button?.title || buttonSecondary?.title)

  // Phones show the mobile image when there is one, otherwise the main image.
  // A mobile image without its own alt text borrows the main image's
  const phoneImage = mobileImage?.asset
    ? { ...mobileImage, alt: mobileImage.alt || image?.alt || '' }
    : hasImage
      ? image
      : undefined

  // Content always sits in the left half on desktop. Without an image the
  // right half is left as plain colour, otherwise the image fills it, its left
  // edge fading into the band colour so the two blend mid-banner. On phones
  // the image runs edge to edge under the text, its top edge fading in the
  // same way
  return (
    <section
      className={clsx('relative overflow-hidden', {
        [backgroundColourClass(backgroundColor)]: backgroundColor,
      })}
    >
      {hasImage && (
        <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
          <Image
            image={image}
            customSize={1440}
            className="size-full object-cover [mask-image:linear-gradient(to_right,transparent,black_20%)]"
          />
        </div>
      )}
      <div className="container relative z-[1] flex items-center lg:min-h-[35rem]">
        <div className="py-12 lg:w-1/2 lg:py-20 lg:pr-8">
          {content && <ContentEditor content={content} />}
          {hasButtons && (
            <div className="flex flex-wrap gap-4 pt-6">
              {button && <Button {...button} />}
              {buttonSecondary && <Button {...buttonSecondary} />}
            </div>
          )}
        </div>
      </div>
      {phoneImage && (
        <div className="lg:hidden">
          <Image
            image={phoneImage}
            customSize={1080}
            className="h-auto w-full [mask-image:linear-gradient(to_bottom,transparent,black_25%)]"
          />
        </div>
      )}
    </section>
  )
}
